import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import { NavigationSlider, Button, Icon } from '@yes.technology/react-toolkit'

import { useBlueprintsContext } from 'modules/interaction/hooks'
import useValidateStep from 'modules/interaction/hooks/useValidateStep/useValidateStep'
import { flatValuesToOldActivities } from 'modules/interaction/utils/flatValuesToOldActivities'

import { useAppDispatch, useAppSelector } from 'state/store'

import {
  deepClone,
  getProperty,
  setProperty
} from '../../../../../utils/helpers/object'

import { interactionModelOperations } from '../../../redux'
import { getStepErrorMessages, showError } from '../../../utils/validation'

import Activity from '../Activity'

import {
  FlatActivityGroup as ActivityGroupType,
  ActivityValue,
  FlatInteractionModelData,
  NestableGroupValue
} from 'types/shared'
import ResetModal from './ResetModal'
import useParentDependency from './useParentDependency'
import useValidateGroup from './useValidateGroup'
import { useGroupValidationContext } from './GroupValidationContext'
import { VALIDATION_STATUSES } from 'utils/constants'
import { Card, ContentWrapper, TitleWrapper } from './ActivityGroup.styles'

const getGroupValues = (
  activityValues: FlatInteractionModelData['activity_values'] = {},
  path: string[]
): NestableGroupValue => {
  const values = getProperty(activityValues, path)

  return Array.isArray(values)
    ? values
    : [
        {
          uuid: '',
          values: {}
        }
      ]
}

export type RuleSetType = {
  depends_on: string
  reset?: 'auto' | boolean
}

export type ActivityGroupProps = {
  currentActivityModel: ActivityGroupType
  parentPath: string[]
  onChange: (activities: string) => void
  label: string
  currentStepIndex: number
  ruleSet?: RuleSetType
}

const ActivityGroup = ({
  currentActivityModel,
  parentPath,
  onChange,
  label,
  currentStepIndex,
  ruleSet
}: ActivityGroupProps) => {
  const interactionModel = useAppSelector((state) => state.interactionModel)

  const values = getGroupValues(interactionModel.flatData?.activity_values, [
    ...parentPath,
    currentActivityModel.uuid
  ])

  const totalElements = values.length || 1

  const [currentPosition, setCurrentPosition] = useState(totalElements)

  const currentValues = values[Math.min(totalElements, currentPosition) - 1]

  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  const { validateStep } = useValidateStep()

  const [confirmModalCallback, setConfirmModalCallback] = useState<() => void>()
  const [cancelModalCallback, setCancelModalCallback] = useState<() => void>()
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)

  const previousTotalElements = useRef(totalElements)

  useEffect(() => {
    const isRemovingGroup = previousTotalElements.current > totalElements

    setCurrentPosition((previousPosition) =>
      isRemovingGroup
        ? Math.min(previousPosition, totalElements)
        : totalElements
    )

    previousTotalElements.current = totalElements
  }, [totalElements])

  const currentGroupPath = useMemo(
    () => [
      ...parentPath,
      currentActivityModel.uuid,
      (currentPosition - 1).toString(),
      'values'
    ],
    [currentActivityModel.uuid, currentPosition, parentPath]
  )

  useParentDependency({
    ruleSet,
    path: currentGroupPath,
    interactionModel,
    values,
    uuid: currentActivityModel.uuid,
    onChange
  })

  const handleAddGroup = async () => {
    const flatData = interactionModel.flatData

    if (!flatData) {
      return
    }

    const currentStep = flatData.steps[currentStepIndex - 1]

    if (!currentStep) {
      return
    }

    const activityModels = flatData.activity_models
    const activityValues = flatData.activity_values
    const stepActivities = [currentActivityModel.uuid]

    const { invalidActivities, updatedActivityValues } = await validateStep({
      stepActivities,
      activityModels,
      activityValues
    })

    if (invalidActivities.length) {
      dispatch(
        interactionModelOperations.updateActivities(
          flatValuesToOldActivities(updatedActivityValues)
        )
      )

      getStepErrorMessages({
        invalidActivities,
        stepName: currentStep.des,
        t
      }).forEach((error) => showError({ error }))

      return
    }

    dispatch(
      interactionModelOperations.addActivityGroup(
        currentActivityModel.uuid,
        parentPath
      )
    )
  }

  const handleRemoveGroup = (removedGroupIndex: number) => {
    dispatch(
      interactionModelOperations.removeActivityGroup(
        currentActivityModel.uuid,
        parentPath,
        removedGroupIndex
      )
    )
  }

  const { getDependencyCallbacks } = useGroupValidationContext()

  const updateActivityValue = useCallback(
    (valueSchemas: [string[], ActivityValue][]) => {
      const [farthestParentId] = currentGroupPath
      const mainGroupValues = deepClone(
        (interactionModel.flatData?.activity_values[
          farthestParentId
        ] as NestableGroupValue) || []
      )

      if (!Array.isArray(mainGroupValues) || !mainGroupValues.length) {
        return
      }

      const updatedValues = {
        [farthestParentId]: mainGroupValues
      }

      valueSchemas.forEach(([path, value]) => {
        setProperty(updatedValues, path, value)
      })

      const currentGroupPositionValue = getGroupValues(
        updatedValues,
        currentGroupPath.slice(0, -2)
      )

      const currentGroupId = currentGroupPath.slice(0, -2).pop()

      if (!currentGroupId || !currentGroupPositionValue) {
        return
      }

      const groupValues = {
        [currentGroupId]: currentGroupPositionValue
      }

      onChange(flatValuesToOldActivities(groupValues)[0].des)
    },
    [currentGroupPath, interactionModel.flatData?.activity_values, onChange]
  )

  const changeActivity = useCallback(
    (uuid: string, value: ActivityValue) => {
      const dependencyCallbacks = getDependencyCallbacks(uuid)
      if (dependencyCallbacks.length) {
        setConfirmModalCallback(() => () => {
          const dependencyArgs = dependencyCallbacks.map((callback) =>
            callback()
          )
          const currentChange: [string[], ActivityValue] = [
            [...currentGroupPath, uuid],
            value
          ]

          updateActivityValue([...dependencyArgs, currentChange])

          setIsResetModalOpen(false)
        })

        setCancelModalCallback(() => () => {
          const currentValue = getProperty(values, [
            (currentPosition - 1).toString(),
            uuid
          ]) as Record<string, ActivityValue | NestableGroupValue>

          const parsedValue =
            typeof currentValue === 'string'
              ? currentValue
              : flatValuesToOldActivities(currentValue)[0].des

          updateActivityValue([[[...currentGroupPath, uuid], parsedValue]])
          setIsResetModalOpen(false)
        })

        setIsResetModalOpen(true)
      }

      updateActivityValue([[[...currentGroupPath, uuid], value]])
    },
    [
      getDependencyCallbacks,
      setConfirmModalCallback,
      setCancelModalCallback,
      setIsResetModalOpen,
      updateActivityValue,
      currentGroupPath,
      values,
      currentPosition
    ]
  )

  const { isFetchingBlueprints } = useBlueprintsContext()

  const hierarchyLevel = parentPath.length / 2 + 1

  const groupActivities = useMemo(
    () =>
      currentActivityModel.activity_models.filter(
        (uuid) =>
          interactionModel.flatData?.activity_models[uuid]?.content_type !==
          'activity_group'
      ),
    [currentActivityModel.activity_models, interactionModel.flatData]
  )

  const { validateActivity, status, isDisabled } = useValidateGroup({
    groupActivities,
    uuid: currentActivityModel.uuid,
    dependency: ruleSet?.depends_on
  })

  const disableAdd = isFetchingBlueprints || isDisabled

  const disableRemove =
    isFetchingBlueprints || totalElements === 1 || isDisabled

  const cardStatus = useMemo(
    () => (status === VALIDATION_STATUSES.INVALID ? 'error' : 'default'),
    [status]
  )

  return (
    <>
      <Card level={hierarchyLevel} disabled={isDisabled} status={cardStatus}>
        <TitleWrapper>
          <div className='d-flex justify-content-between mt-2'>
            <h2 style={{ margin: 0 }}>{label}</h2>
            <div className='d-flex'>
              <Button
                impactClass='navigation'
                onClick={handleAddGroup}
                heightClass='semiSmall'
                style={{
                  width: '32px',
                  marginLeft: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                disabled={disableAdd}
                title={t('activity-group.btn-add-group')}
              >
                <Icon iconName='Plus' icSize='small' />
              </Button>
              <Button
                impactClass='navigation'
                variant='secondary'
                onClick={() => handleRemoveGroup(currentPosition - 1)}
                heightClass='semiSmall'
                disabled={disableRemove}
                title={t('activity-group.btn-remove-group')}
                style={{
                  width: '32px',
                  marginLeft: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Icon iconName='Cleaner' icSize='small' />
              </Button>
            </div>
          </div>
        </TitleWrapper>

        <NavigationSlider
          currentPosition={currentPosition}
          first={1}
          total={totalElements}
          hierarchyLevel='level_3'
          doubleArrowhead={true}
          textForCurrent={t(
            'activity-group.navigation-slider.text-for-current'
          )}
          afterClick={setCurrentPosition}
          heightClass='small'
        />

        <ContentWrapper>
          {currentActivityModel.activity_models.flatMap((uuid) => {
            const activityModelInput =
              interactionModel.flatData?.activity_models[uuid]

            if (!activityModelInput) {
              return []
            }

            const value = currentValues.values[uuid]

            return (
              <Activity
                key={[currentPosition, totalElements, uuid].join(',')}
                activityModelInput={activityModelInput}
                value={Array.isArray(value) ? undefined : value}
                parentPath={currentGroupPath}
                changeActivity={changeActivity}
                validateGroupActivity={validateActivity}
                currentStepIndex={currentStepIndex}
                disabled={isDisabled}
              />
            )
          })}
        </ContentWrapper>
      </Card>
      <ResetModal
        isOpen={isResetModalOpen}
        onConfirm={() => confirmModalCallback?.()}
        onCancel={() => cancelModalCallback?.()}
      />
    </>
  )
}

export default ActivityGroup
